/* eslint-disable max-lines */
import React, {ComponentPropsWithoutRef, useMemo} from "react";
import {Trans, useTranslation} from "react-i18next";
import useChallengeModeTranslation from "../hooks/translation/useChallengeModeTransaltion";
import {gql} from "@apollo/client";
import {
  Project, Challenge, ChallengeEducationState, ChallengeMode, ParticipationDeactivateReason
} from "../schema";
import {reverse} from "../routing";
import {formatDuration, formatToMoscowDate} from "../time";
import {getSharedId} from "../types";

import ReactLink from "./ReactLink";
import Skeleton from "../ui/skeleton";
import {CheckmarkFilledCircleIcon, CloseCircleOutlineIcon, PlayIcon, StatsChartIcon} from "../ui/icons";
import Link from "../ui/link";
import Mutable from "../ui/mutable";
import IconButton from "../ui/iconbutton";

import cn from "classnames";
import classes from "./ChallengeCard.module.css";

export type QueryData = {
  challenge: Pick<
    Challenge,
    "id" | "name" | "canPlay" | "hasPassed" | "mode" | "accessToken" | "activateAt"
    | "participationCreatedAt" | "deactivateReason"
  >;
  project: Pick<Project, "id" | "accessToken">;
  challengeState: Pick<ChallengeEducationState, "status" | "attemptsCount" | "passedAttemptsCount"> & {
    bestAttempt: Pick<NonNullable<ChallengeEducationState["bestAttempt"]>, "passed" | "score" | "time"> | undefined;
    lastAttempt: Pick<NonNullable<ChallengeEducationState["lastAttempt"]>, "passed" | "score" | "time"
      | "startedAt"> | undefined;
  };
};

type props = ComponentPropsWithoutRef<"div"> & {
  challenge: QueryData["challenge"];
  project?: QueryData["project"],
} & (
    | {
        challengeState: QueryData["challengeState"];
        isShared?: false;
      }
    | {
        challengeState?: undefined;
        isShared: true;
      }
  );

export default function ChallengeCard({className, challenge, project, challengeState, isShared, ...props}: props) {
  const {t, i18n} = useTranslation();

  const {activateAt, canPlay, deactivateReason, name} = challenge;

  const {bestAttempt, lastAttempt, attemptsCount, passedAttemptsCount} = challengeState ?? {};

  const isCompetenceTest = challenge.mode === ChallengeMode.COMPETENCE_TEST;
  const summaryAttempt = isCompetenceTest ? lastAttempt ?? bestAttempt : bestAttempt;

  const mode = useChallengeModeTranslation(challenge.mode)

  const linkProps = useMemo(() => {
    const url = new URL(
      reverse("playerPlay", {
        id: isShared ? getSharedId(challenge) : challenge.id,
      }),
      document.baseURI,
    );
    if (isShared && project) {
      url.searchParams.set("projectId", getSharedId(project));
    }
    return {
      // Making relative URL because react-router don't accept absolute
      href: canPlay ? url.pathname + url.search : undefined,
      target: isShared ? "_blank" : "_self",
      title: name,
    };
  }, [canPlay, challenge, project, isShared, name]);

  return (
    <div className={cn(classes.root, className)} {...props}>
      <div className={classes.card}>
        <Mutable
          as={isShared ? "a" : ReactLink}
          className={cn(classes.playArea, {[classes.disabled]: !canPlay})}
          {...linkProps}
        >
          {!canPlay && deactivateReason && (
            <div className={classes.activateAtDate}>
              {deactivateReason === ParticipationDeactivateReason.BY_ACTIVATE_AT && activateAt && (
                <>
                  <div>{t("components.ChallengeCard.notPlay.byActivateAt")}</div>
                  <div>{formatToMoscowDate(activateAt, i18n.language)}</div>
                </>
              )}
              {deactivateReason === ParticipationDeactivateReason.MANUAL && (
                <div>
                  <Trans i18nKey="components.ChallengeCard.notPlay.manual" components={[<br />]} />
                </div>
              )}
              {deactivateReason === ParticipationDeactivateReason.BY_ATTEMPTS && (
                <div>
                  <Trans i18nKey="components.ChallengeCard.notPlay.byAttempts" components={[<br />]} />
                </div>
              )}
              {deactivateReason === ParticipationDeactivateReason.BY_DEACTIVATE_AT && (
                <div>
                  <Trans i18nKey="components.ChallengeCard.notPlay.byDeactivateAt" components={[<br />]} />
                </div>
              )}
            </div>
          )}
          <PlayIcon className={classes.playIcon} />
        </Mutable>
        {!isShared && (
          <div className={classes.resultsArea}>
            <div className={classes.resultsSummary}>
              {summaryAttempt ? (
                <>
                  <div className={classes.bestAttempt}>
                    <span
                      className={cn(
                        classes.resultsSummaryPassed,
                        summaryAttempt.passed ? classes.success : classes.fail,
                      )}
                    >
                      {summaryAttempt.passed
                        ? t("components.ChallengeCard.resultsSummary.sucess")
                        : t("components.ChallengeCard.resultsSummary.fail")}
                    </span>
                    {!isCompetenceTest && (
                      <span>
                        {t("components.ChallengeCard.resultsSummary.highestScore")}: {summaryAttempt.score}/100
                      </span>
                    )}
                    <span>
                      {isCompetenceTest
                        ? t("components.ChallengeCard.resultsSummary.lastAttemptTime")
                        : t("components.ChallengeCard.resultsSummary.bestTime")}
                      : {formatDuration(summaryAttempt.time)}
                    </span>
                  </div>
                  <div className={classes.attemptsCount}>
                    <span>
                      {t("components.ChallengeCard.resultsSummary.attemptsCount")}: {attemptsCount}
                    </span>
                    {!isCompetenceTest && (
                      <span>
                        {t("components.ChallengeCard.resultsSummary.passedAttemptsCount")}: {passedAttemptsCount}
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className={classes.bestAttempt}>
                    <span className={cn(classes.resultsSummaryPassed)}>
                      {t("components.ChallengeCard.resultsSummary.notStarted")}
                    </span>
                  </div>
                  <div className={classes.attemptsCount}>
                    <span>
                      {t("components.ChallengeCard.resultsSummary.attemptsCount")}: {attemptsCount}
                    </span>
                  </div>
                </>
              )}
            </div>
            <div className={classes.resultsSnippet}>
              {summaryAttempt && (
                <div className={classes.resultsWidget}>
                  {summaryAttempt.passed ? (
                    <CheckmarkFilledCircleIcon className={cn(classes.resultIcon, classes.success)} />
                  ) : (
                    <CloseCircleOutlineIcon className={cn(classes.resultIcon, classes.fail)} />
                  )}
                  {!isCompetenceTest && (
                    <span className={classes.bestScore}>{summaryAttempt.score}/100</span>
                  )}
                </div>
              )}
              <IconButton
                as={ReactLink}
                size="l"
                className={classes.resultsButton}
                icon={StatsChartIcon}
                href={reverse("playerResults", {challengeId: challenge.id})}
              />
            </div>
          </div>
        )}
      </div>
      <Link
        as={isShared ? "a" : ReactLink}
        className={cn(classes.link, {[classes.disabled]: !canPlay})}
        {...linkProps}
      >
        {challenge.name ? (
          <span className={classes.name} translate="no">
            {challenge.name}
          </span>
        ) : (
          <span className={cn(classes.name, classes.notSpecified)}>{t("common.notSpecified")}</span>
        )}
        <span className={classes.challengeMode}>{mode}</span>
      </Link>
    </div>
  );
}

ChallengeCard.Skeleton = React.memo(function ({className, ...props}: React.ComponentProps<typeof Skeleton.Block>) {
  return (
    <div className={cn(classes.root, classes.skeleton, className)} {...props}>
      <Skeleton.Block className={classes.card} height={160} />
      <Skeleton.Text className={classes.name} height={23} width={160} />
      <Skeleton.Text className={classes.challengeMode} height={14} width={200}/>
    </div>
  );
});

ChallengeCard.fragments = {
  root: gql`
    fragment ChallengeCard on Challenge {
      id
      name
      accessToken
      activateAt
      participationCreatedAt
      canPlay
      deactivateReason
      hasPassed
      mode
    }
  `,
  project: gql`
    fragment ChallengeCardProject on Project {
      id
      accessToken
    }
  `,
  state: gql`
    fragment ChallengeCardState on ChallengeEducationState {
      status
      isCompleted
      bestAttempt {
        passed
        score
        time
      }
      lastAttempt {
        passed
        score
        time
        startedAt
      }
      attemptsCount
      passedAttemptsCount
    }
  `,
};
